import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box';
import { GenericToolbar } from '../../../components/GenericToolbar';
import { useHistory, useParams } from 'react-router-dom';
import { AppBar, Tab, Tabs } from '@mui/material';
import * as PXBColors from '@brightlayer-ui/colors';
import ZonesConfiguration from './ZonesConfiguration/ZonesConfiguration';
import { DeviceData } from '../../../types/device';
import { systemDetailDefault } from '../AssetDetails';
import { useDispatch, useSelector } from 'react-redux';
import { Selectors } from '../../../selectors';
import ConfigureLuminaires from './LuminariesConfiguration/ConfigureLuminaires';
import SetInputOutput from './SetInputOutput/SetInputOutput';
import TestSchedule from './TestSchedule/TestSchedule';
import { useLanguageLocale } from '../../../hooks/language-locale-hooks';
import { WhiteListDetail, WhiteListZone, WhitelistZoneData } from '../../../state/assets-state';
import { Thunks } from '../../../actions';
import { useToasts } from '../../../data/DataProviders/ToastProvider';
import { RECORD_PER_PAGE_LIMIT } from '../../../constants';
import { AssetActions } from '../../../actions/actions/asset-actions';

export const TAB_STATE_KEY = 'backState';
export const TAB_ZONES = 0;
export const TAB_ZONES_KEY = 'Zones';
export const TAB_CONFIGURE_LUM = 1;
export const TAB_CONFIGURE_LUM_KEY = 'Configure Luminiaires';
export const TAB_TEST_SCHEDULES = 2;
export const TAB_TEST_SCHEDULES_KEY = 'Test Schedules';
export const TAB_SET_INPUT_OUTPUT = 3;
export const TAB_SET_INPUT_OUTPUT_KEY = 'Set Input/Output';

export const getTabState = (): number => {
    const tabState = sessionStorage.getItem(TAB_STATE_KEY);
    switch (tabState) {
        case TAB_ZONES_KEY:
            return TAB_ZONES;
        case TAB_CONFIGURE_LUM_KEY:
            return TAB_CONFIGURE_LUM;
        case TAB_TEST_SCHEDULES_KEY:
            return TAB_TEST_SCHEDULES;
        case TAB_SET_INPUT_OUTPUT_KEY:
            return TAB_SET_INPUT_OUTPUT;
        default:
            return TAB_ZONES;
    }
};

export const updateTabSession = (tab: string): void => {
    sessionStorage.setItem(TAB_STATE_KEY, tab);
};

type Props = {};
const WhiteListTabs: React.FC<React.PropsWithChildren<React.PropsWithChildren<Props>>> = (props: Props) => {
    const history = useHistory();
    const { t } = useLanguageLocale();
    const calledRef = React.useRef(false);
    const dispatch = useDispatch();
    const { addToast } = useToasts();
    const [systemDetail, setSystemDetailState] = React.useState<DeviceData>(systemDetailDefault);
    const asset = useSelector(Selectors.Asset.assets);
    const [value, setValue] = React.useState(getTabState());
    const { deviceId } = useParams<{ deviceId: string }>();
    const [whiteListDetails, setWhiteListDetails] = useState<WhiteListDetail[]>([]);
    const [deviceWhitelistZone, setDeviceWhitelistZone] = useState<WhitelistZoneData | null>(null);
    const [selectedWhitelistId, setSelectedWhitelistId] = useState<string>("");
    const [selectedZone, setSelectedZone] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [rowsPerPage, setRowsPerPage] = useState(RECORD_PER_PAGE_LIMIT);
    const [offset, setOffset] = useState(1);
    const [currentPage, setCurrentPage] = useState(0);

    // Test Schedule pagination state
    const [testScheduleSearchQuery, setTestScheduleSearchQuery] = useState('');
    const [testScheduleRowsPerPage, setTestScheduleRowsPerPage] = useState(RECORD_PER_PAGE_LIMIT);
    const [testScheduleOffset, setTestScheduleOffset] = useState(1);
    const [testScheduleCurrentPage, setTestScheduleCurrentPage] = useState(0);
    const [testScheduleTabIndex, setTestScheduleTabIndex] = useState(0); // 0 for All, 1-4 for Groups
    const [testScheduleTotalCount, setTestScheduleTotalCount] = useState(0); // Store initial total count
    const [testScheduleGroupCounts, setTestScheduleGroupCounts] = useState({ group1: 0, group2: 0, group3: 0, group4: 0 }); // Store initial group counts
    const [originalZoneData, setOriginalZoneData] = useState<{ [id: number]: WhiteListZone }>({});
    const hasInitializedZonesRef = React.useRef(false);

    function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
        const { children, value, index, ...other } = props;
        return <div {...other}>{value === index && <Box p={3}>{children}</Box>}</div>;
    }
    useEffect(() => {
        // Clear all zone-related state when device changes
        setDeviceWhitelistZone(null);
        setOriginalZoneData({});
        setSelectedWhitelistId("");
        setWhiteListDetails([]);
        hasInitializedZonesRef.current = false;
        calledRef.current = false;
    }, [deviceId]);

    useEffect(() => {
        if (!asset.deviceListLoading && asset.deviceListSuccess) {
            const deviceDetails = asset.deviceList.detail.filter((device) => device.systemId === deviceId)[0];
            setSystemDetailState(deviceDetails);
        } else if (!asset.deviceListLoading && asset.deviceListFail) {
            console.log('Device list loading failed');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [asset.deviceListLoading, asset.deviceListSuccess, asset.deviceListFail, deviceId]);

    useEffect(() => {
        if (!asset.getWhitelistDetailLoading && asset.getWhitelistDetailSuccess) {
            // Check if the whitelist belongs to current device
            const currentDeviceWhitelists = asset.getWhitelistDetail.filter(
                (whitelist) => whitelist.systemId === deviceId
            );

            setWhiteListDetails(currentDeviceWhitelists);
        } else if (!asset.getWhitelistDetailLoading && asset.getWhitelistDetailFail) {
            addToast(t('TOAST_MSG.TOAST_ERROR_WHITELIST_FAILS'), 'error');
        }
    }, [asset.getWhitelistDetailLoading, asset.getWhitelistDetailSuccess, asset.getWhitelistDetailFail, asset.getWhitelistDetail, deviceId, addToast, t]);

    useEffect(() => {
        //  Only proceed if we have whitelists for CURRENT device
        if (whiteListDetails.length > 0 && !calledRef.current) {
            const firstWhitelistId = whiteListDetails[0].id;

            // Double check: verify whitelist belongs to current device
            const belongsToCurrentDevice = whiteListDetails[0].systemId === deviceId;

            if (belongsToCurrentDevice) {
                setSelectedWhitelistId(firstWhitelistId);
                dispatch(Thunks.Asset.getDeviceWhitelistZone(firstWhitelistId));
                dispatch(Thunks.Asset.getConfigureLuminairesList(firstWhitelistId, "", [], []));
                calledRef.current = true;
            } else {
                // Clear state
                setSelectedWhitelistId("");
                setDeviceWhitelistZone(null);
                calledRef.current = false;
            }
        } else if (whiteListDetails.length === 0) {
            // Clear state when no whitelists available
            setSelectedWhitelistId("");
            setDeviceWhitelistZone(null);
            calledRef.current = false;
        }
    }, [whiteListDetails, deviceId, dispatch]);


    useEffect(() => {
        // Don't call API if whiteListDetails is empty or selectedWhitelistId is empty
        if (whiteListDetails.length === 0) {
            return;
        }

        if (selectedWhitelistId && selectedWhitelistId !== "") {
            // Check which tab is active and use appropriate pagination/search params
            if (value === TAB_CONFIGURE_LUM) {
                // Configure Luminaires tab
                dispatch(
                    Thunks.Asset.getConfigureLuminairesList(
                        selectedWhitelistId,
                        searchQuery,
                        selectedZone ? [Number(selectedZone)] : [],
                        [],
                        rowsPerPage,
                        offset
                    )
                );
            } else if (value === TAB_TEST_SCHEDULES) {
                // Test Schedule tab
                // Pass testScheduleTabIndex as array if not 0 (All tab means no filter)
                const testGroupFilter = testScheduleTabIndex === 0 ? [] : [testScheduleTabIndex];
                dispatch(
                    Thunks.Asset.getConfigureLuminairesList(
                        selectedWhitelistId,
                        testScheduleSearchQuery,
                        [],
                        testGroupFilter,
                        testScheduleRowsPerPage,
                        testScheduleOffset
                    )
                );
            } else if (value === TAB_SET_INPUT_OUTPUT) {
                // Set Input/Output tab
                dispatch(Thunks.Asset.getIoConfigDetails(selectedWhitelistId));
            }
        } else {
        }
    }, [selectedWhitelistId, searchQuery, selectedZone, rowsPerPage, offset, testScheduleSearchQuery, testScheduleRowsPerPage, testScheduleOffset, testScheduleTabIndex, value, whiteListDetails.length, dispatch]);

    // Initialize zone data when deviceWhitelistZone is loaded
    useEffect(() => {
        if (hasInitializedZonesRef.current) {
            return;
        }

        if (!deviceWhitelistZone || !deviceWhitelistZone.zones || deviceWhitelistZone.zones.length === 0) {
            return;
        }

        const initialZoneData: { [id: number]: WhiteListZone } = {};
        deviceWhitelistZone.zones.forEach((zone) => {
            initialZoneData[Number(zone.id)] = {
                id: zone.id,
                name: zone.name,
                description: zone.description,
                luminariesCount: zone.luminariesCount,
            };
        });

        setOriginalZoneData(initialZoneData);
        hasInitializedZonesRef.current = true;
    }, [deviceWhitelistZone]);

    useEffect(() => {
        // Clear all zone-related state when device changes
        setDeviceWhitelistZone(null);
        setOriginalZoneData({});
        setSelectedWhitelistId("");
        hasInitializedZonesRef.current = false;
        calledRef.current = false;
    }, [deviceId]);

    useEffect(() => {
        if (!asset.deviceGetWhitelistZoneLoading && asset.deviceGetWhitelistZoneSuccess) {
                setDeviceWhitelistZone(asset.deviceGetWhitelistZone);
        } else if (!asset.deviceGetWhitelistZoneLoading && asset.deviceGetWhitelistZoneFail) {
            addToast(t('TOAST_MSG.TOAST_ERROR_DEVICE_WHITELIST_ZONE_FAILS'), 'error');
        }
    }, [asset.deviceGetWhitelistZoneLoading, asset.deviceGetWhitelistZoneSuccess, asset.deviceGetWhitelistZoneFail, asset.deviceGetWhitelistZone, deviceId, addToast, t]);

    updateTabSession(TAB_ZONES_KEY);

    const handleTabChange = (_event: any, newValue: React.SetStateAction<number>) => {
        setValue(newValue);
    };

    useEffect(() => {
        if (deviceId) {
            dispatch(Thunks.Asset.getWhitelistDetail(deviceId, '', '', '', ''));
            // Clear everything when switching devices
            setSelectedWhitelistId("");
            setWhiteListDetails([]); //  Clear old data
            dispatch(AssetActions.getWhitelistZoneUnmounted());
        }
    }, [deviceId, dispatch]);

    const handleCreateWhitelistZoneErrorResponse = (): void => {
        if (asset.whitelistzoneErrorCode !== '') {
            if (asset.whitelistzoneErrorCode === '9280')
                addToast(t('TOAST_MSG.MANAGE_WHITELIST_ZONE_ERROR_CODE_9280'), 'error');
            else if (asset.whitelistzoneErrorCode === '9001')
                addToast(t('TOAST_MSG.MANAGE_WHITELIST_ZONE_ERROR_CODE_9001'), 'error');
            else if (asset.whitelistzoneErrorCode === '9009')
                addToast(t('TOAST_MSG.MANAGE_WHITELIST_ZONE_ERROR_CODE_9009'), 'error');
            else if (asset.whitelistzoneErrorCode === '9047')
                addToast(t('TOAST_MSG.MANAGE_WHITELIST_ZONE_ERROR_CODE_9047'), 'error');
            else if (asset.whitelistzoneErrorCode === '9048')
                addToast(t('TOAST_MSG.MANAGE_WHITELIST_ZONE_ERROR_CODE_9048'), 'error');
            else if (asset.whitelistzoneErrorCode === '9050')
                addToast(t('TOAST_MSG.MANAGE_WHITELIST_ZONE_ERROR_CODE_9050'), 'error');
            else if (asset.whitelistzoneErrorCode === '9273')
                addToast(t('TOAST_MSG.MANAGE_WHITELIST_ZONE_ERROR_CODE_9273'), 'error');
            else if (asset.whitelistzoneErrorCode === '9276')
                addToast(t('TOAST_MSG.MANAGE_WHITELIST_ZONE_ERROR_CODE_9276'), 'error');
            else if (asset.whitelistzoneErrorCode === '9274')
                addToast(t('TOAST_MSG.MANAGE_WHITELIST_ZONE_ERROR_CODE_9274'), 'error');
            else if (asset.whitelistzoneErrorCode === '9275')
                addToast(t('TOAST_MSG.MANAGE_WHITELIST_ZONE_ERROR_CODE_9275'), 'error');
            else if (asset.whitelistzoneErrorCode === '9263')
                addToast(t('TOAST_MSG.MANAGE_WHITELIST_ZONE_ERROR_CODE_9263'), 'error');
            else if (asset.whitelistzoneErrorCode === '9279')
                addToast(t('TOAST_MSG.MANAGE_WHITELIST_ZONE_ERROR_CODE_9279'), 'error');
            else if (asset.whitelistzoneErrorCode === '9278')
                addToast(t('TOAST_MSG.MANAGE_WHITELIST_ZONE_ERROR_CODE_9278'), 'error');
            else
                addToast(t('TOAST_MSG.TOAST_ERROR_SAVE_ZONES_FAILS'), 'error');
        }
    };

    useEffect(() => {
        // Listen for successful zone save
        if (!asset.whitelistzoneLoading && asset.whitelistzoneSuccess) {
            // Use the ID from the save response OR fall back to current selectedWhitelistId
            const savedWhitelistId = asset.whitelistZoneId;

            if (savedWhitelistId) {
                // Just refetch the zone details - NO need to update selectedWhitelistId
                dispatch(Thunks.Asset.getDeviceWhitelistZone(savedWhitelistId));

                // Reset initialization flag so zones reload
                hasInitializedZonesRef.current = false;
            } else {
            }

            if (deviceId) {
                dispatch(Thunks.Asset.getWhitelistDetail(deviceId, '', '', '', ''));
            }
            dispatch(AssetActions.whitelistzoneUnmounted());
            dispatch(AssetActions.getWhitelistZoneUnmounted());

            addToast(asset.whitelistZoneMessage || 'Zones saved successfully', 'success');//AFter successful save
        } else if (!asset.whitelistzoneLoading && asset.whitelistzoneFail) {
            handleCreateWhitelistZoneErrorResponse();

            dispatch(AssetActions.whitelistzoneUnmounted());
            dispatch(AssetActions.getWhitelistZoneUnmounted());
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        asset.whitelistzoneLoading,
        asset.whitelistzoneSuccess,
        asset.whitelistzoneFail,
        asset.whitelistZoneId,
        asset.whitelistZoneMessage,
        selectedWhitelistId,
        deviceId,
        dispatch,
        addToast,
        t
    ]);

    const handleSetLumConfigErrorResponse = (): void => {
        if (asset.setConfigureLuminariesListErrorCode !== '') {
            if (asset.setConfigureLuminariesListErrorCode === '8200')
                addToast(t('TOAST_MSG.SET_CONFIG_LUM_ERROR_CODE_8200'), 'error');
            else if (asset.setConfigureLuminariesListErrorCode === '9001')
                addToast(t('TOAST_MSG.SET_CONFIG_LUM_ERROR_CODE_9001'), 'error');
            else if (asset.setConfigureLuminariesListErrorCode === '9274')
                addToast(t('TOAST_MSG.SET_CONFIG_LUM_ERROR_CODE_9274'), 'error');
            else if (asset.setConfigureLuminariesListErrorCode === '9281')
                addToast(t('TOAST_MSG.SET_CONFIG_LUM_ERROR_CODE_9281'), 'error');
            else if (asset.setConfigureLuminariesListErrorCode === '9008')
                addToast(t('TOAST_MSG.SET_CONFIG_LUM_ERROR_CODE_9008'), 'error');
            else if (asset.setConfigureLuminariesListErrorCode === '9292')
                addToast(t('TOAST_MSG.SET_CONFIG_LUM_ERROR_CODE_9292'), 'error');
            else
                addToast(t('TOAST_MSG.TOAST_ERROR_SAVE_CONFIG_LUMS_FAILS'), 'error');
        }
    };

    const handleSetIOConfigErrorResponse = (): void => {
        if (asset.setIOConfigurationErrorCode !== '') {
            // Add specific error code handling as needed
            if (asset.setIOConfigurationErrorCode === '9001')
                addToast(t('TOAST_MSG.SET_IO_CONFIG_ERROR_CODE_9001'), 'error');
            else if (asset.setIOConfigurationErrorCode === '9274')
                addToast(t('TOAST_MSG.SET_IO_CONFIG_ERROR_CODE_9274'), 'error');
            else
                addToast(asset.setIOConfigurationErrorCode || t('TOAST_MSG.TOAST_ERROR_SAVE_IO_CONFIG_FAILS'), 'error');
        } else {
            addToast(t('TOAST_MSG.TOAST_ERROR_SAVE_IO_CONFIG_FAILS'), 'error');
        }
    };


    useEffect(() => {
        // Listen for successful configure luminaires save
        if (!asset.setConfigureLuminariesListLoading && asset.setConfigureLuminariesListSuccess) {
            addToast(asset.setConfigureLuminariesMessage || 'Luminaires configured successfully', 'success');

            //  Use current values at time of success
            if (selectedWhitelistId) {
                dispatch(
                    Thunks.Asset.getConfigureLuminairesList(
                        selectedWhitelistId,
                        searchQuery,
                        selectedZone ? [Number(selectedZone)] : [],
                        [],
                        rowsPerPage,
                        offset
                    )
                );
            }
            dispatch(AssetActions.setConfigureLuminairesListUnmounted());

        } else if (!asset.setConfigureLuminariesListLoading && asset.setConfigureLuminariesListFail) {
            handleSetLumConfigErrorResponse();
            dispatch(AssetActions.setConfigureLuminairesListUnmounted());
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        asset.setConfigureLuminariesListLoading,
        asset.setConfigureLuminariesListSuccess,
        asset.setConfigureLuminariesListFail,
        asset.setConfigureLuminariesMessage,
    ]);

    useEffect(() => {
        // Listen for successful setIOConfiguration save
        if (!asset.setIOConfigurationLoading && asset.setIOConfigurationSuccess) {
            addToast(asset.setIOConfigurationMessage || 'Configuration saved successfully', 'success');

            // Re-fetch IO config details to get updated data
            if (selectedWhitelistId) {
                dispatch(Thunks.Asset.getIoConfigDetails(selectedWhitelistId));
            }
            dispatch(AssetActions.setIOConfigurationUnmounted());

        } else if (!asset.setIOConfigurationLoading && asset.setIOConfigurationFail) {
            handleSetIOConfigErrorResponse();
            dispatch(AssetActions.setIOConfigurationUnmounted());
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        asset.setIOConfigurationLoading,
        asset.setIOConfigurationSuccess,
        asset.setIOConfigurationFail,
        asset.setIOConfigurationErrorCode,
    ]);

    return (
        <>
            <div data-testid="white-list-tabs">
                <GenericToolbar
                    title={systemDetail.systemName}
                    subtitle={systemDetail.buildingName + ' < ' + systemDetail.projectName}
                    disableButton={!asset.deviceListLoading}
                    showBackButton={true}
                    backButtonOnClick={() => history.goBack()}
                />
                <Box sx={{ width: '100%', typography: 'body1' }}>
                    <AppBar position="static" style={{ backgroundColor: PXBColors.blue[500] }}>
                        <Tabs
                            indicatorColor='secondary'
                            variant="scrollable"
                            scrollButtons={false}
                            textColor="inherit"
                            value={value}
                            onChange={handleTabChange}
                            sx={{
                                backgroundColor: PXBColors.blue[700],
                                height: 2,
                            }}
                        >
                            <Tab data-testid="tabsZones" label={t('DEVICE_DETAILS.ZONES')} style={{ textTransform: 'none', marginLeft: 35, color: PXBColors.white[500] }} />
                            <Tab data-testid="tabsConfigureLuminiaires" label={t('WIRELESS_GATEWAY.WIRELESS_CONFIGURE_LUMINARIES')} style={{ textTransform: 'none', marginLeft: 35, color: PXBColors.white[500] }} />
                            <Tab data-testid="tabsTestSchedules" label={t('ASSETS_DETAILS.TEST_SCHEDULES')} style={{ textTransform: 'none', marginLeft: 35, color: PXBColors.white[500] }} />
                            <Tab data-testid="tabsSetInput/Output" label={t('ASSETS_DETAILS.SET_IP_OP')} style={{ textTransform: 'none', marginLeft: 35, color: PXBColors.white[500] }} />
                        </Tabs>
                    </AppBar>
                    <TabPanel value={value} index={TAB_ZONES} data-testid="tab-zones-container">
                        <ZonesConfiguration
                            key="zones-config"
                            deviceWhitelistZone={deviceWhitelistZone}
                            projectId={systemDetail.projectId}
                            deviceId={systemDetail.systemId}
                            buildingId={systemDetail.buildingId}
                            title={systemDetail.systemName}
                            subtitle={systemDetail.buildingName + ' < ' + systemDetail.projectName}
                            zoneData={originalZoneData}
                        />
                    </TabPanel>
                    <TabPanel value={value} index={TAB_CONFIGURE_LUM} data-testid="tab-configure-luminiaires-container">
                        <ConfigureLuminaires
                            deviceWhitelistZone={deviceWhitelistZone}
                            selectedWhitelistId={selectedWhitelistId}
                            selectedZone={selectedZone}
                            setSelectedZone={setSelectedZone}
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            rowsPerPage={rowsPerPage}
                            setRowsPerPage={setRowsPerPage}
                            offset={offset}
                            setOffset={setOffset}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                        />
                    </TabPanel>
                    <TabPanel value={value} index={TAB_TEST_SCHEDULES} data-testid="tab-test-schedules-container">
                        <TestSchedule
                            deviceWhitelistZone={deviceWhitelistZone}
                            searchQuery={testScheduleSearchQuery}
                            setSearchQuery={setTestScheduleSearchQuery}
                            rowsPerPage={testScheduleRowsPerPage}
                            setRowsPerPage={setTestScheduleRowsPerPage}
                            offset={testScheduleOffset}
                            setOffset={setTestScheduleOffset}
                            currentPage={testScheduleCurrentPage}
                            setCurrentPage={setTestScheduleCurrentPage}
                            testGroupTabIndex={testScheduleTabIndex}
                            setTestGroupTabIndex={setTestScheduleTabIndex}
                            totalLuminairesCount={testScheduleTotalCount}
                            setTotalLuminairesCount={setTestScheduleTotalCount}
                            groupCounts={testScheduleGroupCounts}
                            setGroupCounts={setTestScheduleGroupCounts}
                        />
                    </TabPanel>
                    <TabPanel value={value} index={TAB_SET_INPUT_OUTPUT} data-testid="tab-set-input-output-container">
                        <SetInputOutput />
                    </TabPanel>
                </Box>
            </div>
        </>
    )
}

export default WhiteListTabs
